import { createPortal } from "react-dom";
import { Layers, Sparkles } from "lucide-react";
import {
  FACET_AUTOGENERATE_COUNTDOWN_HOLD_LABEL,
  FACET_AUTOGENERATE_COUNTDOWN_SECONDS,
  FACET_AUTOGENERATE_LOADING_SUBTITLE,
  FACET_AUTOGENERATE_LOADING_TITLE,
} from "../../tars-constants";
import FacetAutogenerateCountdownRing from "./FacetAutogenerateCountdownRing";
import { useFacetAutogenerateCountdown } from "./useFacetAutogenerateCountdown";
import { useFacetAutogenerateLoadingStep } from "./useFacetAutogenerateLoadingStep";

interface FacetAutogenerateLoadingOverlayProps {
  open: boolean;
}

export default function FacetAutogenerateLoadingOverlay({
  open,
}: FacetAutogenerateLoadingOverlayProps) {
  const countdown = useFacetAutogenerateCountdown(open, FACET_AUTOGENERATE_COUNTDOWN_SECONDS);
  const loadingStep = useFacetAutogenerateLoadingStep(open);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/65 px-4 backdrop-blur-md"
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-label={FACET_AUTOGENERATE_LOADING_TITLE}
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-[0_24px_80px_rgba(15,23,42,0.35)]">
        <div className="px-8 pb-8 pt-10 text-center">          <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563eb] to-[#c026d3] text-white shadow-lg shadow-indigo-500/30">
            <Sparkles size={22} aria-hidden />
          </div>

          <h2 className="text-[22px] font-bold tracking-tight text-[#111827]">
            {FACET_AUTOGENERATE_LOADING_TITLE}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            {FACET_AUTOGENERATE_LOADING_SUBTITLE}
          </p>

          <div className="mt-8 flex justify-center">
            <FacetAutogenerateCountdownRing
              remaining={countdown}
              total={FACET_AUTOGENERATE_COUNTDOWN_SECONDS}
              holdLabel={FACET_AUTOGENERATE_COUNTDOWN_HOLD_LABEL}
            />
          </div>

          <div className="mt-8 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-[#374151]">
              <Layers size={15} className="shrink-0 text-[#2563eb]" aria-hidden />
              <span key={loadingStep} className="animate-pulse">
                {loadingStep}…
              </span>
            </div>
          </div>

          <p className="mt-5 text-xs text-gray-400">
            Do not close or refresh this page while generation is in progress.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
